import {addMonths} from 'date-fns/addMonths';
import {startOfMonth} from 'date-fns/startOfMonth';

import {and, formatDate} from '@edna/utils';

import {EChannelType, TItemId} from 'src/constants';
import {EPermission} from 'src/containers/User';
import {ECacheTag, rootApi} from 'src/models';

import {TNextPaymentItems, TPaymentDebtItems} from './PaymentTab/definitions';
import {
  EPaymentType,
  TApproximatePayment,
  TConnectionPayment,
  TInitPayment,
  TLicense,
  TLicenseStatus,
  TRenewalPayment,
} from './definitions';

type TNextPaymentPayload = {
  tenantId?: TItemId;
  additionalMonths?: number;
  statuses?: TLicenseStatus[];
};

type TApproximatePaymentsRecord = {
  [EPaymentType.CONNECTION_FEE]: TConnectionPayment;
  [EPaymentType.INIT]: TInitPayment;
  [EPaymentType.RENEWAL]: TRenewalPayment;
};

export const licenseApi = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => {
    const makePaymentDebtsQuery = (channelType?: EChannelType) =>
      builder.query<TPaymentDebtItems, TItemId | void>({
        query: (tenantId) => ({
          method: 'GET',
          url: tenantId
            ? '/admin/billing/license/payment/debt/groups'
            : '/billing/license/payment/debt/groups',
          params: {
            channelType: channelType?.toUpperCase(),
            tenantId,
          },
          meta: {
            isShowError: true,
            permissions: tenantId
              ? and<EPermission>('PERMISSION_TENANT_TRANSACTION_READ')
              : and<EPermission>('PERMISSION_TRANSACTION_READ'),
          },
        }),
      });

    const makeNextPaymentQuery = (channelType?: EChannelType) =>
      builder.query<TNextPaymentItems, TNextPaymentPayload | void>({
        query: ({tenantId, additionalMonths, statuses} = {}) => ({
          method: 'POST',
          url: tenantId
            ? '/admin/billing/license/payment/next/groups'
            : '/billing/license/payment/next/groups',
          params: {
            channelType: channelType?.toUpperCase(),
            tenantId,
          },
          data: {
            period: formatDate(startOfMonth(addMonths(new Date(), additionalMonths ?? 1)), {
              format: 'yyyy-MM',
            }),
            statuses,
          },
          meta: {
            isShowError: true,
            permissions: tenantId
              ? and<EPermission>('PERMISSION_TENANT_TRANSACTION_READ')
              : and<EPermission>('PERMISSION_TRANSACTION_READ'),
          },
        }),
      });

    return {
      getAllPaymentDebts: makePaymentDebtsQuery(),

      getAllNextPayment: makeNextPaymentQuery(),
      getSmsNextPayment: makeNextPaymentQuery(EChannelType.SMS),
      getWhatsappNextPayment: makeNextPaymentQuery(EChannelType.WHATSAPP),
      getViberNextPayment: makeNextPaymentQuery(EChannelType.VIBER),
      getMaxBotNextPayment: makeNextPaymentQuery(EChannelType.MAX_BOT),
      getMaxNextPayment: makeNextPaymentQuery(EChannelType.MAX),
      getTelegramNextPayment: makeNextPaymentQuery(EChannelType.TELEGRAM),

      getAllLicenses: builder.query<TLicense[], TItemId | void>({
        query: (tenantId) => ({
          method: 'GET',
          url: tenantId ? '/admin/billing/license' : '/billing/license',
          params: {
            tenantId,
          },
          meta: {
            isShowError: true,
            permissions: tenantId
              ? and<EPermission>('PERMISSION_TENANT_LICENSE_READ')
              : and<EPermission>('PERMISSION_READ'),
          },
        }),
        providesTags: [ECacheTag.LICENSES],
      }),
      scheduleCancelLicense: builder.mutation<
        void,
        {licenseId: TLicense['id']; cancelAt: string | Date}
      >({
        query: ({licenseId, cancelAt}) => ({
          method: 'POST',
          url: `/admin/billing/license/${licenseId}/cancel/schedule`,
          data: {
            cancelAt,
          },
          meta: {
            isShowError: true,
            isShowSuccess: true,
            successMessageKey: 'Licenses:cancelModal.successBlock',
            permissions: and<EPermission>('PERMISSION_TENANT_LICENSE_BLOCK'),
          },
        }),
        invalidatesTags: [ECacheTag.LICENSES],
      }),
      rescheduleCancelLicense: builder.mutation<
        void,
        {licenseId: TLicense['id']; cancelAt: string | Date}
      >({
        query: ({licenseId, cancelAt}) => ({
          method: 'PUT',
          url: `/admin/billing/license/${licenseId}/cancel/reschedule`,
          data: {
            cancelAt,
          },
          meta: {
            isShowError: true,
            isShowSuccess: true,
            successMessageKey: 'Licenses:cancelModal.successBlock',
            permissions: and<EPermission>('PERMISSION_TENANT_LICENSE_BLOCK'),
          },
        }),
        invalidatesTags: [ECacheTag.LICENSES],
      }),
      unscheduleCancelLicense: builder.mutation<void, TLicense['id']>({
        query: (licenseId) => ({
          method: 'POST',
          url: `/admin/billing/license/${licenseId}/cancel/unschedule`,
          meta: {
            isShowError: true,
            isShowSuccess: true,
            successMessageKey: 'Licenses:confirmCancelModal.successCancel',
            permissions: and<EPermission>('PERMISSION_TENANT_LICENSE_BLOCK'),
          },
        }),
        invalidatesTags: [ECacheTag.LICENSES],
      }),

      getApproximateLicensePayments: builder.query<TApproximatePaymentsRecord, TItemId>({
        query: (availableTariffId) => ({
          method: 'GET',
          url: '/billing/license/payment/next/approximate',
          params: {
            availableTariffId,
          },
          meta: {
            isShowError: true,
          },
        }),
        transformResponse: (payments: TApproximatePayment[]) =>
          payments.reduce(
            (acc, payment) => ({
              ...acc,
              [payment.paymentType]: payment,
            }),
            {},
          ) as TApproximatePaymentsRecord,
      }),
    };
  },
});

export const {
  useGetAllPaymentDebtsQuery,

  useGetAllNextPaymentQuery,
  useGetSmsNextPaymentQuery,
  useGetWhatsappNextPaymentQuery,
  useGetViberNextPaymentQuery,
  useGetMaxBotNextPaymentQuery,
  useGetMaxNextPaymentQuery,
  useGetTelegramNextPaymentQuery,

  useGetAllLicensesQuery,
  useScheduleCancelLicenseMutation,
  useRescheduleCancelLicenseMutation,
  useUnscheduleCancelLicenseMutation,

  useGetApproximateLicensePaymentsQuery,
} = licenseApi;
